// Cash profit + profit sharing math. Pure functions so the numbers can be
// tested and explained.
//
//   Payments received  = deposits dated in the period (Deposits page, Job
//                        Estimate Spreadsheet, Bank Statement money-in tied to
//                        a WO). Bank "money in" is NOT used, because transfers
//                        between accounts would inflate it.
//   Business money out = for each monthly bank summary in the period:
//                        money out − owner draws − transfers.
//                        Owner reimbursable counts as business. Any money out
//                        not broken down counts as business ("Uncategorized").
//   Cash profit        = payments received − business money out
//   Payout             = cash profit × employee %   (0 when profit ≤ 0)

import { lineKind, REPAYMENT_TYPE } from "./bankSpendCategories";

const num = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};
const round2 = (n) => Math.round(n * 100) / 100;

export const monthKey = (y, m) => `${y}-${String(m).padStart(2, "0")}`;

/** ["2026-07","2026-08","2026-09"] for Q3 2026, or ["2026-09"] for a month. */
export const monthsInPeriod = (period) => {
  if (period.type === "year") return Array.from({ length: 12 }, (_, i) => monthKey(period.year, i + 1));
  if (period.type === "quarter") {
    const start = (period.quarter - 1) * 3 + 1;
    return [0, 1, 2].map((i) => monthKey(period.year, start + i));
  }
  return [period.month];
};

export const periodLabel = (period) => {
  if (period.type === "year") return `${period.year}`;
  if (period.type === "quarter") return `Q${period.quarter} ${period.year}`;
  const [y, m] = period.month.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
};

export const periodKey = (period) =>
  period.type === "year" ? `${period.year}` : period.type === "quarter" ? `${period.year}-Q${period.quarter}` : period.month;

/** Break one account-month summary into kinds. */
export const summarizeMonth = (summary, categories) => {
  const moneyOut = num(summary?.moneyOut);
  const lines = Array.isArray(summary?.lines) ? summary.lines : [];
  const byKind = { business: 0, owner_reimbursable: 0, owner_draw: 0, transfer: 0, repayment: 0 };
  const byCategory = {};
  lines.forEach((l) => {
    const amt = num(l.amount);
    if (!amt) return;
    const kind = lineKind(l, categories);
    byKind[kind] += amt;
    const key = l.category || "Uncategorized";
    if (!byCategory[key]) byCategory[key] = { category: key, kind, amount: 0 };
    byCategory[key].amount += amt;
  });
  const allocated = Object.values(byKind).reduce((a, b) => a + b, 0);
  const uncategorized = Math.max(0, moneyOut - allocated);
  const overAllocated = Math.max(0, allocated - moneyOut);
  const businessOut = byKind.business + byKind.owner_reimbursable + uncategorized;
  return {
    moneyIn: num(summary?.moneyIn),
    reimbursableIn: num(summary?.reimbursableIn),
    moneyOut,
    allocated: round2(allocated),
    uncategorized: round2(uncategorized),
    overAllocated: round2(overAllocated),
    byKind: Object.fromEntries(Object.entries(byKind).map(([k, v]) => [k, round2(v)])),
    byCategory: Object.values(byCategory),
    businessOut: round2(businessOut),
  };
};

const isInvoiceOnly = (d) => d?.splitInvoiceOnly || d?.invoiceOnly;

/**
 * Everything the Profit Sharing page shows for one period.
 * accounts: bankStatementAccounts docs; personal accounts are not required
 * and not counted (business money only).
 */
export const computeProfit = ({ period, deposits, summaries, accounts, categories, asOf = new Date() }) => {
  const months = monthsInPeriod(period);
  // Statements only exist for finished months, so only those are checked.
  const currentMonth = monthKey(asOf.getFullYear(), asOf.getMonth() + 1);
  const finishedMonths = months.filter((m) => m < currentMonth);
  const inPeriod = (dateStr) => months.includes(String(dateStr || "").slice(0, 7));

  const periodDeposits = deposits.filter((d) => inPeriod(d.date) && !isInvoiceOnly(d) && num(d.amount) !== 0);
  const paymentsReceived = round2(periodDeposits.reduce((s, d) => s + num(d.amount), 0));

  const businessAccounts = accounts.filter((a) => a.accountType !== "personal");
  const businessIds = new Set(businessAccounts.map((a) => a.id));

  const periodSummaries = summaries.filter(
    (s) => months.includes(s.month) && (businessIds.has(s.accountId) || !accounts.some((a) => a.id === s.accountId))
  );

  const totals = { moneyIn: 0, reimbursableIn: 0, moneyOut: 0, businessOut: 0, uncategorized: 0, overAllocated: 0, owner_draw: 0, transfer: 0, repayment: 0, owner_reimbursable: 0, business: 0 };
  const categoryTotals = {};
  periodSummaries.forEach((s) => {
    const m = summarizeMonth(s, categories);
    totals.moneyIn += m.moneyIn;
    totals.reimbursableIn += m.reimbursableIn;
    totals.moneyOut += m.moneyOut;
    totals.businessOut += m.businessOut;
    totals.uncategorized += m.uncategorized;
    totals.overAllocated += m.overAllocated;
    Object.entries(m.byKind).forEach(([k, v]) => (totals[k] += v));
    m.byCategory.forEach((c) => {
      if (!categoryTotals[c.category]) categoryTotals[c.category] = { ...c, amount: 0 };
      categoryTotals[c.category].amount += c.amount;
    });
  });
  Object.keys(totals).forEach((k) => (totals[k] = round2(totals[k])));

  // Which business accounts are missing a summary for which month.
  const missing = [];
  businessAccounts.forEach((a) => {
    finishedMonths.forEach((mo) => {
      if (!summaries.some((s) => s.accountId === a.id && s.month === mo)) missing.push({ account: a, month: mo });
    });
  });
  const monthsWithNoSummaryAtAll = finishedMonths.filter((mo) => !periodSummaries.some((s) => s.month === mo));

  const cashProfit = round2(paymentsReceived - totals.businessOut);

  return {
    months,
    paymentsReceived,
    paymentCount: periodDeposits.length,
    periodDeposits,
    totals,
    categoryTotals: Object.values(categoryTotals).map((c) => ({ ...c, amount: round2(c.amount) })).sort((a, b) => b.amount - a.amount),
    cashProfit,
    missing,
    monthsWithNoSummaryAtAll,
    noAccounts: businessAccounts.length === 0,
    finishedMonths,
    summaryCount: periodSummaries.length,
  };
};

export const computePayouts = (cashProfit, participants) => {
  const base = Math.max(0, num(cashProfit));
  const rows = participants.map((p) => ({ ...p, percent: num(p.percent), payout: round2((base * num(p.percent)) / 100) }));
  const totalPercent = round2(rows.reduce((s, r) => s + r.percent, 0));
  const totalPayout = round2(rows.reduce((s, r) => s + r.payout, 0));
  return { rows, totalPercent, totalPayout, retained: round2(base - totalPayout), base };
};

/* ======================================================================
   Uploaded bank statement lines → monthly summaries
   ----------------------------------------------------------------------
   When a statement PDF has been uploaded for an account and month, those
   lines ARE that month's money in / money out. They replace any typed
   Monthly Summary for the same account + month, so nothing is counted twice.
   ====================================================================== */

export const PERSONAL_ACTION = "owner_draw_personal_do_not_count";
const PERSONAL_CATEGORIES = new Set(["Personal / Owner Draw", "Personal Life Insurance"]);
const TRANSFER_ACTIONS = new Set([
  "credit_card_payment_transfer",
  "internal_transfer_do_not_count",
  "bank_correction_do_not_count",
  "owner_contribution_do_not_count",
  "matched_existing_do_not_count",
]);
const TRANSFER_CATEGORIES = new Set(["Credit Card Payment", "Internal Transfer", "Bank Correction / Reversal"]);

export const entryIsPersonal = (e = {}) =>
  e.accountingAction === PERSONAL_ACTION || (!e.accountingAction && PERSONAL_CATEGORIES.has(e.category));

const entryIsTransfer = (e = {}) =>
  TRANSFER_ACTIONS.has(e.accountingAction) || (!e.accountingAction && TRANSFER_CATEGORIES.has(e.category));

/** Personal sub-type saved on the line, e.g. "Personal Entertainment". */
export const personalTypeOf = (e = {}) =>
  e.personalType || (e.category === "Personal Life Insurance" ? "Personal Insurance" : "Personal");

const normText = (v) => String(v || "").trim().toLowerCase();
const last4Of = (v) => String(v || "").replace(/\D/g, "").slice(-4);

/** Which saved account a bank line belongs to (older lines may only have bank name + last 4). */
export const entryAccountId = (entry = {}, accounts = []) => {
  if (entry.bankAccountId) return entry.bankAccountId;
  const found = accounts.find(
    (a) => normText(a.bankName) === normText(entry.bankName) && last4Of(a.accountLast4) === last4Of(entry.accountLast4)
  );
  if (found) return found.id;
  return `unassigned:${normText(entry.bankName) || "unknown"}|${last4Of(entry.accountLast4)}`;
};

const entryMonth = (e = {}) => {
  const d = e.date?.toDate ? e.date.toDate().toISOString() : String(e.date || "");
  return d.slice(0, 7);
};

const entryCategory = (e = {}) => {
  const c = e.category || "Other";
  const other = String(e.categoryOther || "").trim();
  return c === "Other" && other ? other : c;
};

/** One summary per account + month, built from uploaded / entered bank lines. */
export const summariesFromEntries = (entries = [], accounts = []) => {
  const map = new Map();
  entries.forEach((e) => {
    const month = entryMonth(e);
    if (!/^\d{4}-\d{2}$/.test(month)) return;
    const accountId = entryAccountId(e, accounts);
    const key = `${accountId}_${month}`;
    if (!map.has(key)) {
      const acct = accounts.find((a) => a.id === accountId);
      map.set(key, {
        id: key,
        accountId,
        accountType: acct?.accountType || e.accountType || "operating",
        month,
        moneyIn: 0,
        reimbursableIn: 0,
        moneyOut: 0,
        lines: new Map(),
        entryCount: 0,
        source: "upload",
      });
    }
    const s = map.get(key);
    const amt = num(e.amount);
    s.entryCount += 1;
    if (e.type === "moneyIn") {
      s.moneyIn += amt;
      if (e.reimbursable) s.reimbursableIn += amt;
      return;
    }
    s.moneyOut += amt;
    let kind = "business";
    let category = entryCategory(e);
    if (entryIsPersonal(e)) {
      category = personalTypeOf(e);
      kind = category === REPAYMENT_TYPE ? "repayment" : "owner_draw";
    } else if (entryIsTransfer(e)) {
      kind = "transfer";
    }
    const lk = `${kind}|${category}`;
    if (!s.lines.has(lk)) s.lines.set(lk, { id: lk, category, kind, amount: 0 });
    s.lines.get(lk).amount += amt;
  });
  return [...map.values()].map((s) => ({
    ...s,
    moneyIn: round2(s.moneyIn),
    reimbursableIn: round2(s.reimbursableIn),
    moneyOut: round2(s.moneyOut),
    lines: [...s.lines.values()].map((l) => ({ ...l, amount: round2(l.amount) })),
  }));
};

/** Uploaded months win over typed Monthly Summaries for the same account + month. */
export const combineSummaries = (manual = [], uploaded = []) => {
  const uploadedKeys = new Set(uploaded.map((s) => `${s.accountId}_${s.month}`));
  return [
    ...uploaded,
    ...manual.filter((s) => !uploadedKeys.has(`${s.accountId}_${s.month}`)).map((s) => ({ ...s, source: s.source || "manual" })),
  ];
};

/** "2026-03", "2026-04" … the `count` full months before the current month (newest first). */
export const recentCompletedMonths = (count = 6, today = new Date()) => {
  const out = [];
  for (let i = 1; i <= count; i++) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
};

/**
 * Reimbursable money: money in marked Reimbursable (owner's personal money or a
 * loan) minus money out marked "Paying Back Reimbursable". Uses the raw bank
 * lines so each item keeps its date and note.
 */
export const reimbursableActivity = (entries = [], months = null) => {
  const inRange = (e) => !months || months.includes(entryMonth(e));
  const received = entries.filter((e) => e.type === "moneyIn" && e.reimbursable && inRange(e));
  const repaid = entries.filter((e) => e.type !== "moneyIn" && entryIsPersonal(e) && personalTypeOf(e) === REPAYMENT_TYPE && inRange(e));
  const sum = (list) => round2(list.reduce((t, e) => t + num(e.amount), 0));
  return { received, repaid, receivedTotal: sum(received), repaidTotal: sum(repaid), balance: round2(sum(received) - sum(repaid)) };
};
